/* eslint-disable */
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Card,
  CardContent,
  Divider,
  IconButton,
  DialogActions,
} from "@mui/material";
import {
  Search as SearchIcon,
  LocationOn as MapPinIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
} from "@mui/icons-material";
import { PropertyFilters } from "./PropertyFilters";
import { useState, useEffect, useRef } from "react";
import { ScrollReveal } from "./ScrollAnimation";
import { masterDataAPI, propertyAPI } from "../services/api";

const MIN_PRICE = 1000000;
const MAX_PRICE = 500000000;
const ALLOWED_DISTRICTS = ["Udupi", "Dakshina Kannada"];

const formatPriceRangeLabel = (value) => {
  if (value >= 10000000) {
    const crores = value / 10000000;
    return `₹${Number.isInteger(crores) ? crores : crores.toFixed(1)}Cr`;
  }

  return `₹${(value / 100000).toFixed(1)}L`;
};

export function HeroSection({ filters, onFiltersChange, onSearch }) {
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [priceRanges, setPriceRanges] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [tempFilters, setTempFilters] = useState(filters);
  const [locationSearch, setLocationSearch] = useState("");

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const response = await masterDataAPI.getAllMasterData();
        const data = response.data.data;
        setPropertyTypes(data.propertyTypes || []);
        setPriceRanges(data.priceRanges || []);
        // Use districtsWithTaluks from master data
        const allDistricts = data.districtsWithTaluks || [];
        setDistricts(
          allDistricts
            .filter((district) => ALLOWED_DISTRICTS.includes(district.value))
            .map((district) => ({ value: district.value, label: district.label }))
        );
      } catch (error) {
        console.error("Error fetching master data:", error);
      }
    };
    fetchMasterData();
  }, []);

  // Initialize temp filters when modal opens
  useEffect(() => {
    if (isAdvancedSearchOpen) {
      setTempFilters(filters);
    }
  }, [isAdvancedSearchOpen, filters]);

  const updateFilter = (key, value) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const updateTempFilter = (key, value) => {
    setTempFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    onFiltersChange(tempFilters);
    setIsAdvancedSearchOpen(false);
  };

  const cancelFilters = () => {
    setTempFilters(filters);
    setIsAdvancedSearchOpen(false);
  };

  const toggleAmenity = (amenity) => {
    const newAmenities = filters.amenities.includes(amenity)
      ? filters.amenities.filter((a) => a !== amenity)
      : [...filters.amenities, amenity];
    updateFilter("amenities", newAmenities);
  };

  const handleLocationSearch = () => {
    if (locationSearch.trim() && onSearch) {
      onSearch(locationSearch.trim());
    }
  };

  const clearAllFilters = () => {
    onFiltersChange({
      priceRange: [MIN_PRICE, MAX_PRICE],
      propertyType: "",
      propertySubType: "",
      district: "",
      taluk: "",
      bedrooms: "",
      bathrooms: "",
      amenities: [],
      sortBy: "newest"
    });
    setLocationSearch('');
  };

  // Only propagate empty locationSearch when user explicitly clears it, not on initial mount
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (locationSearch === '' && onSearch) {
      onSearch('');
    }
  }, [locationSearch]);

  return (
    <Box
      component="section"
      id="hero-filters-section"
      sx={{
        position: "relative",
        color: "white",
        py: { xs: 5, md: 8 },
        overflow: "hidden",
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://images.unsplash.com/photo-1715764290573-2abd4f43b94b?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxjb2FzdGFsJTIwS2FybmF0YWthJTIwcGFsbSUyMHRyZWVzJTIwYmVhY2h8ZW58MXx8fHwxNzU4NzA3NjkyfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Container maxWidth="lg">
        <ScrollReveal>
          <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontSize: { xs: "2.5rem", md: "4rem", xl: "4rem", xxl: "4rem" },
                fontWeight: 700,
                mb: 3,
                textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
              }}
            >
              Find your perfect place in the Karavali Region
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: "1.25rem", md: "1.5rem" },
                color: "rgba(255, 255, 255, 0.9)",
                maxWidth: 600,
                mx: "auto",
                textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
              }}
            >
              Discover premium properties across Karnataka's coastal region -
              from beachfront villas to commercial spaces and agricultural land
            </Typography>
          </Box>
        </ScrollReveal>

        <ScrollReveal delay={0.3}>
          <Paper
            elevation={8}
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(20px)",
              borderRadius: 4,
              p: { xs: 2, md: 3 },
              maxWidth: 1000,
              mx: "auto",
              border: "1px solid rgba(255, 255, 255, 0.3)",
            }}
          >
            {/* Main Search Section */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: "text.primary",
                  textAlign: "center",
                }}
              >
                🔍 Search Your Dream Property
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        mb: 0.5,
                        display: "block",
                      }}
                    >
                      Location
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        fullWidth
                        placeholder="Enter location..."
                        variant="outlined"
                        size="small"
                        value={locationSearch}
                        onChange={(e) => setLocationSearch(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            handleLocationSearch();
                          }
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <MapPinIcon sx={{ fontSize: 20 }} color="primary" />
                            </InputAdornment>
                          ),
                          endAdornment: locationSearch ? (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                aria-label="Clear location"
                                onClick={() => setLocationSearch("")}
                                sx={{
                                  color: "primary.main",
                                  bgcolor: "grey.300",
                                  borderRadius: 1,
                                  "&:hover": {
                                    bgcolor: "grey.400",
                                  },
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "white",
                          },
                        }}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleLocationSearch}
                        sx={{
                          minWidth: "auto",
                          px: 2,
                          textTransform: "none",
                          fontWeight: 600,
                        }}
                      >
                        <SearchIcon sx={{ fontSize: 20 }} />
                      </Button>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        mb: 0.5,
                        display: "block",
                      }}
                    >
                      Property Type
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={filters.propertyType || ""}
                        displayEmpty
                        onChange={(e) =>
                          updateFilter("propertyType", e.target.value)
                        }
                        sx={{
                          bgcolor: "white",
                        }}
                      >
                        <MenuItem value="">All Types</MenuItem>
                        {propertyTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        mb: 0.5,
                        display: "block",
                      }}
                    >
                      District
                    </Typography>
                    <FormControl fullWidth size="small">
                      <Select
                        value={filters.district || ""}
                        displayEmpty
                        onChange={(e) => updateFilter("district", e.target.value)}
                        sx={{
                          bgcolor: "white",
                        }}
                      >
                        <MenuItem value="">All Districts</MenuItem>
                        {districts.map((district) => (
                          <MenuItem key={district.value} value={district.value}>
                            {district.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Price Range Section */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  💰 Price Range
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Chip
                    label={formatPriceRangeLabel(filters.priceRange[0])}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Typography variant="body2" sx={{ mx: 0.5 }}>
                    -
                  </Typography>
                  <Chip
                    label={formatPriceRangeLabel(filters.priceRange[1])}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: "rgba(59, 130, 246, 0.05)",
                  borderRadius: 2,
                }}
              >
                <Slider
                  value={filters.priceRange}
                  onChange={(_, value) => updateFilter("priceRange", value)}
                  max={MAX_PRICE}
                  min={MIN_PRICE}
                  step={500000}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => formatPriceRangeLabel(value)}
                />
              </Box>
            </Box>

            {/* Active Filters */}
            {((filters.propertyType && filters.propertyType !== "all") ||
              (filters.district && filters.district !== "all") ||
              filters.priceRange[0] > MIN_PRICE ||
              filters.priceRange[1] < MAX_PRICE ||
              locationSearch) && (
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                    }}
                  >
                    Active Filters:
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={clearAllFilters}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "white",
                      bgcolor: "primary.main",
                      borderRadius: 2,
                      px: 1.5,
                      py: 0.5,
                      minHeight: 'auto',
                      "&:hover": {
                        bgcolor: "primary.dark",
                      },
                    }}
                  >
                    Clear All
                  </Button>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  {locationSearch && (
                    <Chip
                      label={`Location: ${locationSearch}`}
                      size="small"
                      color="primary"
                      onDelete={() => setLocationSearch('')}
                    />
                  )}
                  {filters.propertyType && filters.propertyType !== "all" && (
                    <Chip
                      label={filters.propertyType}
                      size="small"
                      color="primary"
                      onDelete={() => updateFilter("propertyType", "")}
                    />
                  )}
                  {filters.district && filters.district !== "all" && (
                    <Chip
                      label={filters.district}
                      size="small"
                      color="primary"
                      onDelete={() => updateFilter("district", "")}
                    />
                  )}
                  {(filters.priceRange[0] > MIN_PRICE ||
                    filters.priceRange[1] < MAX_PRICE) && (
                    <Chip
                      label={`${formatPriceRangeLabel(
                        filters.priceRange[0]
                      )} - ${formatPriceRangeLabel(filters.priceRange[1])}`}
                      size="small"
                      color="primary"
                      onDelete={() => updateFilter("priceRange", [MIN_PRICE, MAX_PRICE])}
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* Advanced Search Button */}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="outlined"
                size="medium"
                onClick={() => setIsAdvancedSearchOpen(true)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 4,
                  borderWidth: 2,
                  "&:hover": {
                    borderWidth: 2,
                  },
                }}
                startIcon={<SearchIcon />}
              >
                Advanced Search
              </Button>
              <Dialog
                open={isAdvancedSearchOpen}
                onClose={() => setIsAdvancedSearchOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                  sx: {
                    borderRadius: 3,
                  },
                }}
              >
                <DialogTitle
                  sx={{
                    fontWeight: 600,
                    fontSize: "1.5rem",
                    borderBottom: 1,
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pr: 1,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <FilterIcon sx={{ color: "primary.main" }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Advanced Search
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => setIsAdvancedSearchOpen(false)}
                    sx={{
                      color: "text.secondary",
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                  <PropertyFilters
                    filters={tempFilters}
                    onFiltersChange={updateTempFilter}
                    showHeader={false}
                  />
                </DialogContent>
                <DialogActions
                  sx={{
                    px: 3,
                    py: 2,
                    borderTop: 1,
                    borderColor: "divider",
                    gap: 2,
                  }}
                >
                  <Button
                    onClick={cancelFilters}
                    variant="outlined"
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      minWidth: 120,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={applyFilters}
                    variant="contained"
                    startIcon={<FilterIcon />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      minWidth: 120,
                    }}
                  >
                    Apply Filters
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          </Paper>
        </ScrollReveal>

        {/* <ScrollReveal delay={0.6}>
          <Grid container spacing={4} sx={{ mt: 8, textAlign: "center" }}>
            {[
              // { number: "2,500+", text: "Properties Listed" },
              // { number: "1,800+", text: "Happy Customers" },
              // { number: "15+", text: "Cities Covered" },
              // { number: "24/7", text: "Customer Support" },
            ].map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Card
                  sx={{
                    bgcolor: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(10px)",
                    borderRadius: 3,
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      bgcolor: "rgba(255, 255, 255, 0.15)",
                    },
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        color: "white",
                        fontSize: { xs: "1.75rem", md: "2.5rem" },
                      }}
                    >
                      {stat.number}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "rgba(255, 255, 255, 0.9)",
                        mt: 1,
                      }}
                    >
                      {stat.text}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </ScrollReveal> */}
      </Container>
    </Box>
  );
}
